import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
            : 'bg-transparent'
        }`}
        role="banner"
      >
        <div className="w-full">
          {/* Top bar */}
          <div className={`${isScrolled ? 'py-2' : 'py-4'} px-4 md:px-6 transition-all duration-300`}>
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center gap-2 flex-shrink-0"
                aria-label="黑料吃瓜网首页"
              >
                <div className="w-8 h-8 md:w-10 md:h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                  <i className="ri-fire-line text-background-50 text-lg md:text-xl" />
                </div>
                <div className="flex flex-col">
                  <span
                    className={`font-heading font-bold leading-tight transition-colors ${
                      isScrolled ? 'text-foreground-950' : 'text-background-50'
                    }`}
                  >
                    <span className="text-sm md:text-lg">黑料吃瓜网</span>
                  </span>
                  <span
                    className={`text-[10px] md:text-xs leading-none transition-colors ${
                      isScrolled ? 'text-foreground-500' : 'text-background-300'
                    }`}
                  >
                    全网热门吃瓜资讯
                  </span>
                </div>
              </Link>

              {/* Desktop Nav */}
              <nav
                className="hidden md:flex items-center gap-1"
                role="navigation"
                aria-label="主导航"
              >
                <Link
                  to="/"
                  className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    location.pathname === '/'
                      ? 'text-primary-500'
                      : isScrolled
                        ? 'text-foreground-700 hover:text-primary-500'
                        : 'text-background-100 hover:text-background-50'
                  }`}
                >
                  首页
                </Link>
                {categories.slice(0, 4).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                      location.pathname.startsWith(`/category/${cat.slug}`)
                        ? 'text-primary-500'
                        : isScrolled
                          ? 'text-foreground-700 hover:text-primary-500'
                          : 'text-background-100 hover:text-background-50'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
                <Link
                  to="/about"
                  className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    location.pathname === '/about'
                      ? 'text-primary-500'
                      : isScrolled
                        ? 'text-foreground-700 hover:text-primary-500'
                        : 'text-background-100 hover:text-background-50'
                  }`}
                >
                  关于我们
                </Link>
              </nav>

              {/* Search + Mobile Menu */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsSearchOpen(!isSearchOpen)}
                  className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full transition-colors ${
                    isScrolled
                      ? 'text-foreground-600 hover:bg-background-100'
                      : 'text-background-50 hover:bg-background-50/20'
                  }`}
                  aria-label="搜索"
                >
                  <i className="ri-search-line text-lg" />
                </button>

                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className={`md:hidden w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                    isScrolled
                      ? 'text-foreground-600 hover:bg-background-100'
                      : 'text-background-50 hover:bg-background-50/20'
                  }`}
                  aria-label="菜单"
                  aria-expanded={isMobileMenuOpen}
                >
                  <i
                    className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Search bar */}
          {isSearchOpen && (
            <div className="px-4 md:px-6 pb-3 animate-in slide-in-from-top-2 duration-200">
              <form onSubmit={handleSearch} className="relative max-w-xl mx-auto">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索吃瓜内容..."
                  className="w-full px-4 py-2.5 pr-10 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                  autoFocus
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-foreground-400 hover:text-primary-500 transition-colors"
                  aria-label="搜索"
                >
                  <i className="ri-search-line" />
                </button>
              </form>
            </div>
          )}
        </div>
      </header>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute top-0 right-0 w-[280px] h-full bg-background-50 shadow-xl overflow-y-auto">
            <div className="p-5 pt-16">
              <nav className="flex flex-col gap-1" role="navigation" aria-label="移动端导航">
                <Link
                  to="/"
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-home-line mr-2" />
                  首页
                </Link>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith(`/category/${cat.slug}`)
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} mr-2`} />
                    {cat.name}
                  </Link>
                ))}
                <div className="my-2 border-t border-background-200/70" />
                <Link
                  to="/about"
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/about'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-information-line mr-2" />
                  关于我们
                </Link>
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
}